import axios from "axios";

const BACKEND_URL = window.location.origin;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Attach admin token from sessionStorage on each request
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("hb_admin_token");
  if (token) config.headers["X-Admin-Token"] = token;
  return config;
});

export const fetchProducts = (params = {}) => api.get("/products", { params }).then(r => r.data);
export const fetchProduct = (id) => api.get(`/products/${id}`).then(r => r.data);
export const fetchMeta = () => api.get("/meta").then(r => r.data);
export const createOrder = (payload) => api.post("/orders", payload).then(r => r.data);
export const fetchOrders = () => api.get("/orders").then(r => r.data);
export const lookupPincode = (pin) =>
  api.get(`/pincode/${pin}`).then(r => r.data);
export const createProduct = (payload) => api.post("/products", payload).then(r => r.data);
export const updateProduct = (id, payload) => api.put(`/products/${id}`, payload).then(r => r.data);
export const deleteProduct = (id) => api.delete(`/products/${id}`).then(r => r.data);
export const razorpayCreateOrder = (amount) => api.post("/razorpay/create-order", { amount }).then(r => r.data);
export const razorpayVerify = (payload) => api.post("/razorpay/verify", payload).then(r => r.data);

export const fetchReviews = (params = {}) => api.get("/reviews", { params }).then(r => r.data);
export const updateReview = (id, payload) => api.put(`/reviews/${id}`, payload).then(r => r.data);
export const deleteReview = (id) => api.delete(`/reviews/${id}`).then(r => r.data);

export const getSetting = (key) => api.get(`/settings/${key}`).then(r => r.data);
export const setSetting = (key, value) => api.put(`/settings/${key}`, { value }).then(r => r.data);

export const adminLogin = (password) => api.post("/admin/login", { password }).then(r => r.data);

// ----- Customer auth -----
export const signupUser = (payload) => api.post("/auth/signup", payload).then(r => r.data);
export const loginUser = (payload) => api.post("/auth/login", payload).then(r => r.data);
export const logoutUser = () => api.post("/auth/logout").then(r => r.data);
export const fetchMe = () => api.get("/auth/me").then(r => r.data);
export const googleExchange = (session_id) => api.post("/auth/google/session", { session_id }).then(r => r.data);
export const phoneVerify = (id_token, full_name = "") => api.post("/auth/phone/verify", { id_token, full_name }).then(r => r.data);

// ----- Customer orders -----
export const fetchMyOrders = () => api.get("/my-orders").then(r => r.data);
export const cancelOrder = (id, reason) => api.post(`/orders/${id}/cancel`, { reason }).then(r => r.data);
export const adminUpdateOrderStatus = (id, status, reason = "") =>
  api.put(`/orders/${id}/status`, { status, reason }).then(r => r.data);
export const adminDeleteOrder = (id) => api.delete(`/orders/${id}`).then(r => r.data);
export const adminReorderProducts = (product_ids) =>
  api.post("/admin/products/reorder", { product_ids }).then(r => r.data);

// ----- Reissue requests -----
export const createReissueRequest = (payload) => api.post("/reissue-requests", payload).then(r => r.data);
export const fetchReissueCount = (product_id) => api.get(`/products/${product_id}/reissue-count`).then(r => r.data);
export const adminListReissueRequests = () => api.get("/admin/reissue-requests").then(r => r.data);
export const adminUpdateReissue = (id, status) => api.put(`/admin/reissue-requests/${id}`, { status }).then(r => r.data);
export const adminDeleteReissue = (id) => api.delete(`/admin/reissue-requests/${id}`).then(r => r.data);

// ----- Coupons -----
export const validateCoupon = (code, subtotal) =>
  api.post("/coupons/validate", { code, subtotal }).then(r => r.data);
export const fetchAutoApplyCoupon = () => api.get("/coupons/auto-apply").then(r => r.data);
export const adminListCoupons = () => api.get("/admin/coupons").then(r => r.data);
export const adminCreateCoupon = (payload) => api.post("/admin/coupons", payload).then(r => r.data);
export const adminUpdateCoupon = (id, payload) => api.put(`/admin/coupons/${id}`, payload).then(r => r.data);
export const adminDeleteCoupon = (id) => api.delete(`/admin/coupons/${id}`).then(r => r.data);

// Search
export const searchProducts = (q) => api.get("/search", { params: { q } }).then(r => r.data);

// Bundle offers
export const fetchBundleOffers = () => api.get("/bundle-offers").then(r => r.data);
export const fetchBundleForProduct = (product_id) =>
  api.get(`/bundle-offers/for-product/${product_id}`).then(r => r.data);
export const adminListBundleOffers = () =>
  api.get("/bundle-offers", { params: { active_only: false } }).then(r => r.data);
export const adminCreateBundleOffer = (payload) =>
  api.post("/admin/bundle-offers", payload).then(r => r.data);
export const adminUpdateBundleOffer = (id, payload) =>
  api.put(`/admin/bundle-offers/${id}`, payload).then(r => r.data);
export const adminDeleteBundleOffer = (id) =>
  api.delete(`/admin/bundle-offers/${id}`).then(r => r.data);

export const formatINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

/**
 * Resolve a media URL returned by our backend.
 * Relative paths (`/api/files/...`) are prefixed with REACT_APP_BACKEND_URL so they work
 * across preview, deployed, and custom domain environments.
 */
export const resolveMediaUrl = (url) => {
  if (!url) return url;
  if (typeof url !== "string") return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};
